import type {
  TranscriptValidationReason,
  UploadErrorCode,
} from "@/lib/analytics/types";

type ErrorWithMessage = {
  message?: string;
};

type ErrorWithCode = {
  code?: string;
};

type ErrorWithResponse = {
  response?: {
    status?: number;
    data?: {
      detail?: string;
    };
  };
};

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as ErrorWithMessage).message ?? "").toLowerCase();
  }
  if (typeof error === "string") {
    return error.toLowerCase();
  }
  return "";
}

function extractErrorCode(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    return String((error as ErrorWithCode).code ?? "").toLowerCase();
  }
  return "";
}

function extractHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "response" in error) {
    return (error as ErrorWithResponse).response?.status;
  }
  return undefined;
}

export function normalizeUploadErrorCode(error: unknown): UploadErrorCode {
  const message = extractErrorMessage(error);
  const code = extractErrorCode(error);
  const status = extractHttpStatus(error);

  if (status === 400 || message.includes("invalid file content")) {
    return "backend_validation_failed";
  }
  if (status === 401 || status === 403 || message.includes("auth")) {
    return "auth_required";
  }
  if (message.includes("unsupported file type")) {
    return "unsupported_file_type";
  }
  if (message.includes("too small")) {
    return "file_too_small";
  }
  if (message.includes("too large")) {
    return "file_too_large";
  }
  if (message.includes("not enough space")) {
    return "not_enough_space";
  }
  if (
    code === "aborted" ||
    code === "err_canceled" ||
    message.includes("cancel")
  ) {
    return "upload_aborted";
  }
  if (message.includes("failed to create record")) {
    return "record_create_failed";
  }
  if (
    status === 408 ||
    status === 429 ||
    (typeof status === "number" && status >= 500) ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("fetch")
  ) {
    return "network_error";
  }
  return "unknown_error";
}

export type TranscriptValidationContext = {
  missingName?: boolean;
  missingContent?: boolean;
  contentTooLarge?: boolean;
  utterancesNone?: boolean;
  utterancesTooFew?: boolean;
  utterancesTooMany?: boolean;
  durationTooShort?: boolean;
  durationTooLong?: boolean;
  parserMismatch?: boolean;
};

export function normalizeTranscriptValidationReason(
  context: TranscriptValidationContext
): TranscriptValidationReason {
  if (context.missingName) {
    return "missing_name";
  }
  if (context.missingContent) {
    return "missing_content";
  }
  if (context.contentTooLarge) {
    return "content_too_large";
  }
  if (context.durationTooLong) {
    return "duration_too_long";
  }
  if (context.durationTooShort) {
    return "duration_too_short";
  }
  if (context.utterancesTooMany) {
    return "utterances_too_many";
  }
  if (context.utterancesTooFew) {
    return "utterances_too_few";
  }
  if (context.utterancesNone) {
    return "utterances_none";
  }
  if (context.parserMismatch) {
    return "parser_mismatch";
  }
  return "unknown_validation_error";
}

